import { CSSProperties, FC, useEffect, useState } from 'react';
import { mainButton } from '@telegram-apps/sdk-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTonAddress } from '@tonconnect/ui-react';

import { useBackButtonToPrevRoute } from '@/hooks/useBackButtonToPrevRoute.tsx';
import { routes } from '@/navigation/routes.tsx';
import { useTonStore } from '@/store/useTonStore.ts';
import { helpers } from '@/helpers.ts';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import { Toast } from '@/components/Toast/Toast.tsx';
import TonController from '@/api/ton/ton.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';

const WithdrawTonDetails: FC = () => {
  useBackButtonToPrevRoute(routes.withdrawTon, false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const userFriendlyAddress = useTonAddress();

  const withdrawAmount =
    (location.state as { requestedWithdrawAmount?: string })
      ?.requestedWithdrawAmount || '0';

  const { balance } = useTonStore();
  const balanceTon = helpers.convertMicrosToTON(balance?.ton_micros) || '0';

  const [initialBalance] = useState(balanceTon);

  const balanceNum = parseFloat(initialBalance);
  const withdrawNum = parseFloat(withdrawAmount);
  const balanceAfter = (balanceNum - withdrawNum).toFixed(4);

  useEffect(() => {
    mainButton.setParams({
      isVisible: true,
      backgroundColor: '#000000',
      text: t('button.confirm')
    });

    mainButton.onClick(handleButtonClick);

    return () => {
      mainButton.offClick(handleButtonClick);
      mainButton.setParams({
        isVisible: false
      });
    };
  }, []);

  const handleButtonClick = async () => {
    mainButton.setParams({ isLoaderVisible: true, isEnabled: false });

    try {
      await TonController.requestPayout({
        ton_micros: helpers.convertTONToMicros(withdrawNum),
        wallet_address: userFriendlyAddress
      });
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_SUCCESS,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_SUCCESS} />
      });
      navigate(routes.withdrawTonSuccess);
    } catch (e) {
      catchErrors(e, true);
    } finally {
      mainButton.setParams({ isLoaderVisible: false, isEnabled: true });
    }
  };

  return (
    <>
      <InputContent
        title={t('earn.withdraw.details_title')}
        style={{ marginTop: 10 }}
      >
        <div style={containerStyle}>
          <div style={contentStyle}>
            <>
              <div style={labelStyle}>{t('earn.withdraw.address')}</div>
              <div style={valueStyle}>{userFriendlyAddress}</div>
            </>
            <hr style={dividerStyle} />
            <>
              <div style={labelStyle}>{t('earn.withdraw.network_fee')}</div>
              <div style={valueStyle}>≈ 0.0025 TON</div>
            </>
            <hr style={dividerStyle} />
            <>
              <div style={labelStyle}>{t('earn.withdraw.total_received')}</div>
              <div style={valueStyle}>{withdrawAmount} TON</div>
            </>
            <hr style={dividerStyle} />
            <>
              <div style={labelStyle}>{t('earn.withdraw.network')}</div>
              <div style={valueStyle}>TON</div>
            </>
          </div>
        </div>
      </InputContent>
      <InputContent style={{ marginTop: 10 }}>
        <div style={containerStyle}>
          <div style={contentStyle}>
            <>
              <div style={labelStyle}>{t('earn.withdraw.balance_after')}</div>
              <div style={valueStyle}>{balanceAfter} TON</div>
            </>
          </div>
        </div>
      </InputContent>
    </>
  );
};

const containerStyle: CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: '8px'
};

const contentStyle: CSSProperties = {
  padding: '12px'
};

const labelStyle: CSSProperties = {
  color: '#808080',
  fontSize: '13px'
};

const valueStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  wordBreak: 'break-word',
  maxWidth: '100%'
};

const dividerStyle: CSSProperties = {
  color: '#808080',
  opacity: 0.1
};

export default WithdrawTonDetails;
